import PropTypes from "prop-types";
import React, { memo, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useUserData } from "Flocknote/apiHooks";

import { useCellMeasurements } from "Boards";
import { useOpenSacramentModal } from "Boards/Cells/Dynamic/SacramentFields/useOpenSacramentModal";
import extendedFieldDataTransformers from "ExtendedFields/dataTransformers";
import classNames from "classnames";

import { useDebounce } from "Utilities/Hooks/useDebounce";

import ExtendedFieldPopover from "./ExtendedFieldPopover";
import SacramentNotePopover from "./SacramentFields/SacramentNotePopover";
import {
  cellContainerStyles,
  editableCellStyles,
  focusVisibleCellStyles,
} from "./cellContainerStyles";
import { getValueOffJson } from "./conf";

const INLINE_CHAR_LIMIT = 100;

const TextCell = memo(WrappedComponent);
export default TextCell;

WrappedComponent.propTypes = {
  columnID: PropTypes.number,
  entityID: PropTypes.number,
  value: PropTypes.string,
  rawValue: PropTypes.string,
  saveCellValue: PropTypes.func,
  canEdit: PropTypes.bool,
  isInitiallyRendered: PropTypes.bool,
  extendedFieldData: PropTypes.object,
  fieldTypeInstanceStructureGroupingID: PropTypes.number,
};

function WrappedComponent({
  columnID,
  entityID,
  entityType,
  value = "",
  rawValue,
  saveCellValue,
  canEdit,
  isInitiallyRendered,
  extendedFieldData = {},
  fieldTypeInstanceStructureGroupingID,
}) {
  const buttonRef = useRef();
  const inputRef = useRef();
  const [{ canEditSacraments }] = useUserData();
  const [inputVal, setInputVal] = useState(value);
  const [mostRecentValue, setMostRecentValue] = useState(value);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasInputChanged, setHasInputChanged] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [showSacramentNotePopover, setShowSacramentNotePopover] =
    useState(false);

  const isSacramentField =
    extendedFieldData?.fieldTypeInstanceStructureGroupingID === 1;
  const isSacramentNote =
    isSacramentField && extendedFieldData?.title?.includes("Notes");

  const isExtendedField =
    extendedFieldData?.type === extendedFieldDataTransformers.fieldTypes.text;
  const isMultiline = Boolean(isExtendedField && extendedFieldData.multiline);

  // Used to update cell value when a Shared Field cell value is updated
  useEffect(
    function overrideInputValWithPropVal() {
      const newValuePropReceived =
        value !== mostRecentValue && value !== inputVal;

      // only update value if needed and cell is not in focus.
      if (newValuePropReceived && !hasFocus) {
        setInputVal(value);
        setMostRecentValue(value);
      }
    },
    [value, inputVal, mostRecentValue, hasFocus]
  );

  let debouncedInputVal = useDebounce(inputVal, 500);
  useEffect(() => {
    if (!hasFocus) return; // only save if updated directly (not via shared cell change)
    if (debouncedInputVal === value) return; // don't save if value hasn't changed

    saveCellValue({
      columnID,
      entityID,
      value: transformValueToSaveShape(debouncedInputVal),
      origValue: transformValueToSaveShape(value),
    });
  }, [hasFocus, debouncedInputVal, value, saveCellValue, columnID, entityID]);

  const { openSacramentModal } = useOpenSacramentModal({
    targetUserID: entityID,
    defaultFieldTypeInstanceMapping:
      extendedFieldData?.defaultFieldTypeInstanceMapping,
    networkFieldID: extendedFieldData?.parentNetworkFieldID,
  });

  const handleClick = () => {
    if (!isEditable) return;

    if (isSacramentField) {
      if (!canEditSacraments) return;
      openSacramentModal();
      return;
    }

    if (isMultiline && canEdit) {
      setShowPopover((prev) => !prev);
      return;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key !== "Enter") return;

    e.preventDefault();
    handleClick();
  };

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    if (newVal?.length > INLINE_CHAR_LIMIT) return;
    setInputVal(newVal);
    setHasInputChanged(true);
    remeasureCell(inputVal);
  };

  const blurTheInput = (e) => {
    e.preventDefault();
    inputRef.current.blur();
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") blurTheInput(e);
  };

  const handleFocus = () => setHasFocus(true);

  const handleBlur = () => {
    // need to use a setTimeout in order to not block debouced save call
    setTimeout(() => {
      setHasFocus(false);
    }, 1000);
  };

  const handleMultiLineSave = (val) => {
    const transformedVal = getValueOffJson(val);
    setInputVal(transformedVal);
    setHasInputChanged(true);
    remeasureCell(transformedVal);
  };

  const handleMouseEnter = () => {
    if (!isEditable) return;
    if (!isSacramentNote) return;
    if (!inputVal?.length) return;
    setShowSacramentNotePopover(true);
  };
  const handleMouseLeave = () => {
    if (!isEditable) return;
    if (!isSacramentNote) return;
    if (!inputVal?.length) return;
    setShowSacramentNotePopover(false);
  };

  const { MeasureCell, remeasureCell } = useCellMeasurements({ columnID });

  let extendedFieldPopoverId = false;
  if (isMultiline) {
    extendedFieldPopoverId = `${
      extendedFieldData?.title ?? "multiline-text"
    }-${entityID}-${columnID}`
      .replaceAll(" ", "-")
      .toLowerCase();
  }

  const isEditable = (() => {
    // This try/catch handles if rawValue is not a valid JSON string,
    // like signup confirmations and other values that are just strings.
    try {
      return JSON.parse(rawValue)?.isEditable ?? true;
    } catch {
      return true;
    }
  })();

  const canEditValue = isSacramentField
    ? canEditSacraments && canEdit
    : canEdit;
  const inputFieldIsDisabled =
    !canEdit || isMultiline || isSacramentField || !isEditable;

  return (
    <>
      <Wrapper
        title={inputVal}
        ref={buttonRef}
        onClick={handleClick}
        onKeyDown={handleKeyPress}
        {...(isMultiline || isSacramentField
          ? {
              role: "combobox",
              tabIndex: 0,
              "aria-expanded": `${showPopover}`,
              "aria-haspopup": "dialog",
              "aria-controls": extendedFieldPopoverId,
            }
          : {})}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <form onSubmit={blurTheInput}>
          <Input
            data-cy="textCell"
            ref={inputRef}
            value={inputVal}
            disabled={inputFieldIsDisabled}
            canEdit={canEditValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={classNames({
              "is-multiline": isMultiline,
              "is-sacrament": isSacramentField,
              "can-edit-sacrament":
                isSacramentField && canEditSacraments && isEditable,
            })}
            onKeyDown={handleInputKeyDown}
            onClick={(e) => {
              if (inputFieldIsDisabled) e.stopPropagation();
            }}
          />
        </form>
        <ExtendedFieldPopover
          id={extendedFieldPopoverId}
          buttonRef={buttonRef}
          showPopover={showPopover}
          setShowPopover={setShowPopover}
          field={extendedFieldData}
          initialValue={
            !(isMultiline && rawValue) ? rawValue : JSON.parse(rawValue).value
          }
          cellData={{ columnID, entityID }}
          onSave={handleMultiLineSave}
        />
        {isSacramentNote && (
          <SacramentNotePopover
            show={showSacramentNotePopover}
            content={rawValue}
            onShouldClose={() => setShowSacramentNotePopover(false)}
            buttonRef={buttonRef}
          />
        )}
      </Wrapper>
      {(isInitiallyRendered || hasInputChanged) && inputVal && (
        <MeasureCell>
          <MeasureStyling>{inputVal}</MeasureStyling>
        </MeasureCell>
      )}
    </>
  );
}

function transformValueToSaveShape(val) {
  return { value: val };
}

const Wrapper = styled.div`
  ${focusVisibleCellStyles};
  box-sizing: border-box;
  cursor: pointer;
  position: relative;
  pointer-events: auto;
`;

const Input = styled.input`
  ${cellContainerStyles};

  ${editableCellStyles}

  &.is-multiline:not(.is-sacrament) {
    // preserve the border despite the actual input being disabled
    border: 1px solid rgba(0, 0, 0, 0.05);
    // thanks Firefox ;)
    pointer-events: none;

    &:hover {
      cursor: pointer;
    }
  }

  &.is-sacrament.can-edit-sacrament {
    pointer-events: none;

    &:hover {
      cursor: pointer;
    }
  }

  &.is-sacrament:not(.can-edit-sacrament):hover {
    cursor: default;
  }
`;

const MeasureStyling = styled.div`
  font-family: "Open Sans", sans-serif;
  white-space: nowrap;
  padding-right: 20px;
  font-size: 13px;
  font-weight: 400;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: none;
  box-sizing: border-box;
`;
